import { IonIcon, IonPage, IonSpinner, IonText, useIonRouter } from "@ionic/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { Button } from "@/components/Global/Button";
import { Content } from "@/components/Global/Content";
import { Capacitor } from "@/lib/capacitor";
import { Dialog } from "@/lib/capacitor/Dialog";
import { arrowLeftStartOnRectangleIcon } from "@/lib/icons/@heroicons/react/16/solid";
import { envelopeIcon, exclamationTriangleIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { queryOptions_myAccess } from "@/lib/query/functions/user/access";
import { useMe } from "@/lib/query/functions/user/me";
import { Routes } from "@/lib/router";
import { authSelectors, useAuthStore } from "@/lib/store/auth";
import { useCountdown } from "@/utils/hooks/useCountdown";

export function NoAccess() {
	const { t } = useTranslation();
	const router = useIonRouter();
	const params = Routes.NoAccess.useParams();
	const isAuthenticated = useAuthStore(useShallow(authSelectors.isAuthenticated));

	const [count, controllers] = useCountdown({ countStart: 10 });

	const [me] = useMe();
	const queryAccess = useQuery({
		...queryOptions_myAccess(),
		enabled: isAuthenticated,
	});
	const hasAccess = queryAccess.data?.hasAccess;

	useEffect(() => {
		if (hasAccess === true) {
			router.push(params.path ?? Routes.Dashboard(), "none", "replace");
		}
	}, [hasAccess, params.path]);

	useEffect(() => {
		controllers.startCountdown();

		return () => {
			controllers.stopCountdown();
		};
	}, []);

	return (
		<IonPage>
			<Content className="part-scroll:items-center part-scroll:justify-center">
				<div className="*:text-brown-700 flex w-full flex-col items-center justify-center gap-2">
					<IonIcon
						icon={exclamationTriangleIcon}
						className="bg-brown-100 rounded-full p-4 text-4xl"
					/>
					<IonText className="text-3xl font-bold">{t("no-access.title")}</IonText>
					<IonText>{t("no-access.description")}</IonText>
					<IonText className="text-brown-700 font-medium">
						{t("no-access.contact")}{" "}
						<a
							href="mailto:jp@stijlbreuk.nl"
							target="_blank"
							rel="noreferrer"
							className="ms-0.5 inline-flex translate-y-px items-center gap-1 underline hover:underline-offset-2 active:underline-offset-2"
						>
							<IonIcon icon={envelopeIcon} className="size-3.5 translate-y-px" />
							{"jp@stijlbreuk.nl"}
						</a>
					</IonText>
				</div>

				<Button
					onClick={() => {
						controllers.resetCountdown();
						queryAccess.refetch();
					}}
					disabled={queryAccess.isFetching || count !== 0}
					className="min-h-0"
				>
					{queryAccess.isFetching && (
						<IonSpinner slot="start" className="-my-8 ms-0 me-2 scale-75" />
					)}{" "}
					{t("no-access.try-again", { count })}
				</Button>

				<Button
					variant="secondary"
					onClick={() =>
						new Promise((resolve) => {
							if (Capacitor.Platform !== "web") {
								Dialog.confirm({
									title: t("auth.sign-out.dialog.title"),
									message: t("auth.sign-out.dialog.message"),
									okButtonTitle: t("auth.sign-out.dialog.actions.confirm"),
									cancelButtonTitle: t("auth.sign-out.dialog.actions.cancel"),
								}).then((confirmed) => {
									if (confirmed) {
										resolve(undefined);
									}
								});
							} else {
								resolve(undefined);
							}
						}).then(useAuthStore.getState().unauthenticate)
					}
				>
					<IonIcon slot="start" icon={arrowLeftStartOnRectangleIcon} className="-my-1 size-4" />
					{t("auth.sign-out.name")}
				</Button>

				<pre className="bg-brown-100 mx-auto mt-8 w-full rounded-lg p-4 font-mono text-sm">
					<strong>{"Email"}:</strong> {me?.email}
					<br />
					<strong>{"Access"}:</strong> {hasAccess ? "Yes" : "No"}
					<br />
					<strong>{"Date"}:</strong> {new Date(queryAccess.dataUpdatedAt).toLocaleString()}
				</pre>
			</Content>
		</IonPage>
	);
}
